import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

const { FiMenu, FiX, FiChevronDown, FiPhone } = FiIcons;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [trialsOpen, setTrialsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setTrialsOpen(false);
  }, [location]);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Our Mission', path: '/our-mission' },
    { name: 'Participate', path: '/contact' },
  ];

  const trialLinks = [
    { name: 'All Current Trials', path: '/current-trials' },
    { name: 'Male Infertility Trial', path: '/male-infertility' },
    { name: 'Ulcerative Colitis Trial', path: '/ulcerative-colitis' },
    { name: 'Type 1 Diabetes Trial', path: '/diabetes' },
    { name: 'Asthma Trial', path: '/asthma' }
  ];

  const isActive = (path) => {
    return location.pathname === path ? "text-primary-600 font-bold" : "text-slate-600 hover:text-primary-600";
  };

  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-2 border-b border-primary-100' : 'bg-white py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 sm:gap-4 group">
            <div className="relative overflow-hidden p-1.5 bg-white rounded-lg border border-primary-50">
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1766272205181-logo%20dr.%20ali.png" 
                alt="Smart Medical Research Logo" 
                className="h-10 sm:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </div>
            <div className="block">
              <p className="text-sm sm:text-lg font-bold text-primary-900 font-poppins leading-tight">
                SMART MEDICAL
              </p>
              <p className="text-xs sm:text-base font-semibold text-primary-600 font-poppins">
                RESEARCH
              </p>
            </div>
          </Link>

          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`text-sm font-semibold transition-colors duration-200 font-poppins relative group ${isActive(item.path)}`}
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-primary-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
              </Link>
            ))}

            <div className="relative group">
              <button 
                className={`flex items-center text-sm font-semibold transition-colors duration-200 font-poppins ${
                  location.pathname.includes('trial') ? "text-primary-600 font-bold" : "text-slate-600 hover:text-primary-600"
                }`}
                onClick={() => setTrialsOpen(!trialsOpen)}
                onMouseEnter={() => setTrialsOpen(true)}
              >
                Current Trials
                <SafeIcon icon={FiChevronDown} className={`ml-1 w-4 h-4 transition-transform duration-200 ${trialsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              <AnimatePresence>
                {trialsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-primary-50 overflow-hidden py-2"
                    onMouseLeave={() => setTrialsOpen(false)}
                  >
                    {trialLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        className="block px-4 py-3 text-sm text-slate-700 hover:bg-primary-50 hover:text-primary-700 transition-colors font-poppins border-l-4 border-transparent hover:border-primary-400"
                        onClick={() => setTrialsOpen(false)}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Link
              to="/contact"
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-poppins flex items-center gap-2"
            >
              <SafeIcon icon={FiPhone} className="w-4 h-4" />
              Contact Us
            </Link>
          </nav>

          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-primary-600 hover:text-primary-800 p-2 transition-colors focus:outline-none"
              aria-label="Toggle menu"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-7 h-7" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white border-t border-primary-50 overflow-hidden shadow-2xl"
          >
            <div className="px-4 py-6 space-y-4 max-h-[80vh] overflow-y-auto">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`block px-4 py-3 rounded-lg text-base font-bold transition-colors font-poppins ${
                    location.pathname === item.path ? "bg-primary-50 text-primary-700" : "text-slate-700 hover:bg-primary-50"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              
              <div className="pt-2 pb-2">
                <div className="px-4 text-xs font-bold text-primary-400 uppercase tracking-wider mb-2 font-poppins">
                  Clinical Trials
                </div>
                {trialLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`block px-4 py-3 rounded-lg text-base font-semibold transition-colors font-poppins ml-2 border-l-2 border-primary-100 hover:border-primary-500 ${
                      location.pathname === link.path ? "text-primary-700 border-primary-500 bg-primary-50" : "text-slate-600 hover:bg-primary-50"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>

              <div className="pt-4 border-t border-slate-100">
                <Link
                  to="/contact"
                  className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-4 rounded-xl font-bold hover:bg-primary-700 transition-colors shadow-lg font-poppins"
                >
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                  Contact Us
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;